import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { ErrorManagement } from '@printdeal/error-management/react';
// @ts-expect-error - still using index.js
import { updateMetric } from '@cimpress-technology/recommendation-library';
import { SESSION_STORAGE_KEYS } from '../../../gatsby/constants';
import { RecommendationsConfig } from '../../types/Configuration';
import type { AnalyticsTrackerContextInterface } from '../AnalyticsTracker/AnalyticsTrackerContext';
import {
  publishEventToSegment,
  preparePayloadForListViewed,
  preparePayloadForProductClicked,
  EVENT_TYPES_FOR_CT_RECOMMENDATIONS,
} from '../AnalyticsTracker/AnalyticsTrackerHelper';
import type { EventDataPayload, EventType } from '../AnalyticsTracker/AnalyticsTrackerHelper';
import type { ConsentData } from '../../helpers/CookiebotHelper';
import type {
  Placement, DatalayerProducts, InViewRecommendation,
  ProductsToDisplay, RecommendationProductDetailPage,
} from './types';

const Events = {
  eec: 'gaEventEec',
};

const Categories = {
  productRecommender: 'product_recommender',
  eCommerce: 'ecommerce',
};

const Actions = {
  click: 'click',
  view: 'view',
  impressions: 'impressions',
};

const FOR_YOU_PAGE_RECOMMENDATIONS = 'recommended_for_you';

const recommendationViewed = (
  analyticsTrackerData: AnalyticsTrackerContextInterface,
  recommendationId: string,
  analyticsId: string,
  placementId: string,
  productsForDataLayer: DatalayerProducts[],
  anonymousCOAMToken: string | undefined,
  config: RecommendationsConfig | null,
  consentData: ConsentData | undefined,
  eventType?: EventType,
) => {
  const [, dispatchToAnalyticsTracker] = analyticsTrackerData;
  /**
   * Calling updateMetric() from ct recommendations API
   */
  const payload = {
    token: anonymousCOAMToken,
    tenant: config?.tenant,
    recommendationId,
    action: 'viewed',
  };
  updateMetric(payload);
  addDataLayerEntry(Events.eec, {
    gaEvent: {
      eventCategory: Categories.eCommerce,
      eventAction: Actions.impressions,
      eventLabel: undefined,
      eventValue: undefined,
      eventNonInteraction: false,
    },
    [Categories.eCommerce]: {
      impressions: productsForDataLayer,
    },
  });

  const PayloadForRecommendationListViewed = {
    trackingIds: {
      recommendationId,
      analyticsId,
      placementId,
      userId: config?.customerId,
    },
  };

  /**
   * Dispatching TrackingIDs to AnalyticsTracker Context
   */
  dispatchToAnalyticsTracker('CT_ADD_NEW_TRACKING_DATA', PayloadForRecommendationListViewed);

  /**
   * Prepairing payload for "Recommendation List Viewed" Event
   */
  const listViewedPayload: EventDataPayload = preparePayloadForListViewed(
    recommendationId,
    placementId,
    productsForDataLayer,
    config,
    consentData,
  );

  /**
  * Publising payload for "Recommendation List Viewed" Event
  * Event publsihed to Segment for Recommendation Engine not for traits
  */
  publishEventToSegment(eventType ?? EVENT_TYPES_FOR_CT_RECOMMENDATIONS.LIST_VIEWED, listViewedPayload);
};

const productClicked = (
  analyticsTrackerData: AnalyticsTrackerContextInterface,
  recommendationId: string,
  product: RecommendationProductDetailPage,
  analyticsId: string,
  placementId: string,
  productPosition: number,
  anonymousCOAMToken: string | undefined,
  config: RecommendationsConfig | null,
  consentData: ConsentData | undefined,
) => {
  const [, dispatchToAnalyticsTracker] = analyticsTrackerData;
  // Calling the method to inform that Recommendation was clicked
  const payload = {
    token: anonymousCOAMToken,
    tenant: config?.tenant,
    recommendationId,
    action: 'clicked',
  };
  const {
    selector,
    productName,
    productCategory,
  } = product;

  updateMetric(payload);

  // Update SessionStorage
  sessionStorage.setItem(SESSION_STORAGE_KEYS.LIST_NAME, analyticsId);
  sessionStorage.setItem(SESSION_STORAGE_KEYS.POSITION, `${productPosition}`);

  // Update DataLayer
  addDataLayerEntry(Events.eec, {
    gaEvent: {
      eventCategory: Categories.eCommerce,
      eventAction: Actions.click,
      eventLabel: productName || undefined,
      eventValue: undefined,
      eventNonInteraction: false,
    },
    [Categories.eCommerce]: {
      click: {
        actionField: {
          list: analyticsId,
        },
        products: [{
          name: productName || undefined,
          id: selector?.product?.key ? selector.product.key : '',
          dimension39: productName || undefined,
          category: productCategory && productCategory.categoryName ? productCategory.categoryName : '',
          position: `${productPosition}`,
        }],
      },
    },
  });

  const payloadForProductClickedTrail = preparePayloadForProductClicked(
    recommendationId,
    product,
    analyticsId,
    productPosition,
    placementId,
    config,
    consentData,
  );

  /**
  * Publising payload for "Recommendation Clicked" Event
  * Event publsihed to Segment for Recommendation Engine not for traits
  */
  publishEventToSegment(EVENT_TYPES_FOR_CT_RECOMMENDATIONS.PRODUCT_CLICKED, payloadForProductClickedTrail);

  /**
  * Dispatching Product Clicked Trail from the placement to AnalyticsTracker Context
  */
  dispatchToAnalyticsTracker('CT_ADD_NEW_TRACKING_DATA_FOR_EVENTS_TRAIL', {
    productId: product.contentful_id,
    placementId,
    trailName: 'productsClicked',
  });
};

export const observerCallback = (
  analyticsTrackerData: AnalyticsTrackerContextInterface,
  inView: boolean,
  entry: IntersectionObserverEntry,
  anonymousCOAMToken: string | undefined,
  config: RecommendationsConfig | null,
  onView: Function,
  consentData?: ConsentData | undefined,
  eventType?: EventType,
) => {
  if (inView) {
    const {
      products, analyticsId, placementId, recommendationId,
    } = JSON.parse(entry?.target?.attributes?.getNamedItem('data-recommendations')?.value || '') as InViewRecommendation;
    const productsForDataLayer: DatalayerProducts[] = products.map((product, index: number) => {
      const {
        selector,
        productName,
        productCategory,
        contentful_id: contentfulId,
      } = product;

      return {
        name: productName || undefined,
        id: selector?.product?.key ? selector.product.key : '',
        product_id: contentfulId,
        prUUID: contentfulId,
        dimension39: productName || undefined,
        category: productCategory && productCategory.categoryName ? productCategory.categoryName : '',
        list: analyticsId,
        list_id: analyticsId,
        position: `${index + 1}`,
      };
    });

    recommendationViewed(
      analyticsTrackerData,
      recommendationId,
      analyticsId,
      placementId,
      productsForDataLayer,
      anonymousCOAMToken,
      config,
      consentData,
      eventType,
    );
    onView(products, analyticsId);
  }
};

export const productClickHandlerForDataLayerTrigger = (
  analyticsTrackerData: AnalyticsTrackerContextInterface,
  recommendationId: string,
  entry: RecommendationProductDetailPage,
  analyticsId: string,
  placementId: string,
  productPosition: number,
  anonymousCOAMToken: string | undefined,
  config: RecommendationsConfig | null,
  clickHandler: Function,
  consentData?: ConsentData | undefined,
) => {
  productClicked(
    analyticsTrackerData,
    recommendationId,
    entry,
    analyticsId,
    placementId,
    productPosition,
    anonymousCOAMToken,
    config,
    consentData,
  );
  clickHandler(entry.productName, productPosition, placementId);
};

/**
 *
 * @param data Event data for GA4
 */
const dispatchDataLayerEventOnProductsClickGA4 = (data: {
  eventName: string;
  position: number;
  productName: string;
  placementId?: string;
  placementName?: string;
}) => {
  const {
    eventName, position, productName, placementId, placementName,
  } = data;
  addDataLayerEntry(
    eventName,
    {
      position,
      productName,
      placement_id: placementId,
      placement_name: placementName,
    },
  );
};

/**
 *
 * @param productName Product name of the clicked product from a placement
 * @param position order/postion of the product in placement
 */
export const handleForYouPageProductOnClick = (
  productName: string,
  position: number,
  placementId?: string,
  placementName?: string,
) => {
  const eventData = {
    eventName: FOR_YOU_PAGE_RECOMMENDATIONS,
    position,
    productName,
    placementId,
    placementName,
  };
  dispatchDataLayerEventOnProductsClickGA4(eventData);
};

/**
 *
 * @param defaultConfig
 * @param componentName : Name of the placement
 * @param userId : Account id/ Customer Id of the logged in user or from LS AJS_USER_ID
 * @param anonymousId
 * @param locale Locale Information from intl
 * @param itemId
 * @param placementId
 * @param gaClientId
 * @returns Configuration for the getRecommendations api payload
 */
export const getConfig = (
  defaultConfig: RecommendationsConfig,
  componentName: string,
  locale: string,
  itemId: string | null | undefined,
  placementId: string,
  gaClientId?: string | undefined,
  userId?: string,
  anonymousId?: string | undefined,
): RecommendationsConfig => ({
  ...(
    defaultConfig && {
      channels: defaultConfig.channels,
      lob: defaultConfig.lob,
      enabled: defaultConfig.enabled,
    }
  ),
  locations: componentName,
  anonymousId,
  customerId: userId || '',
  tenant: process.env.GATSBY_RECOMMENDATIONS_TENANT,
  locale,
  ...(
    itemId && {
      item_id: itemId,
      productsOnPage: itemId,
      productsInCart: itemId,
    }
  ),
  ...(
    componentName !== 'Home Page Section 2' && {
      gaClientId,
    }
  ),
  ...(
    placementId && {
      placementId,
    }
  ),
});

/**
 *
 * @returns Transformed product list for the component
 * @param placements
 */
export const getProductsForPlacements = (placements?: Placement[]) => {
  let scenarioProductsToDisplay: ProductsToDisplay | undefined = {};
  if (placements && placements?.length > 0) {
    scenarioProductsToDisplay = placements?.reduce((acc, scenario: Placement) => {
      const filteredProducts = scenario.recommendations
        .filter((product) => product.publishedContent)
        .map((product) => product.publishedContent);
      return {
        ...acc,
        [scenario.recommendationId]: filteredProducts,
      };
    }, {});
  }

  return scenarioProductsToDisplay;
};

/**
 * There is a slightly different payload for products and categories.
 * To generalize the payload, we are using the item key for both products and categories.
 */
export const getItemsListToDisplay = (placementItemsToDisplay: ProductsToDisplay, placementsList?: Placement[]) => {
  if (!placementsList) {
    return [];
  }

  return placementsList.map((placement) =>
    placementItemsToDisplay[placement.recommendationId].map((item) => ({
      id: item.id,
      name: item.productName || item.title,
      path: item.path,
      recommendationId: placement.recommendationId,
      placementAnalyticsId: placement.analyticsId,
      placementId: placement.placementId,
      discounts: item.productDiscounts,
      image: item.featuredImage,
      title: item.productName ? item.productName : item.title,
      entry: item,
      featuredDescription: item.featuredDescription,
      productCategory: item.productCategory?.categoryName,
    }))).flat();
};

/**
 *
 * @param stringifiedJSON string containing single quotes and back ticks
 * @returns formatted string with only double quotes, for JSON Parse
 */
const formatStringForJSONParse = (stringifiedJSON: String) =>
  stringifiedJSON
    .replace(/(\w+):/g, '"$1":') // Enclose keys in double quotes
    .replace(/'([^']+)'/g, '"$1"'); // Enclose string values in double quotes

/**
 *
 * @param name cookie name
 * @returns cookie consent as js object or json
 * @deprecated Use `useConsent()` instead
 */
export const getCookieConsentForRefresh = (name: string): ConsentData | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length > 0) {
    const cookieValue = parts.pop()?.split(';').shift(); // Optional chaining
    if (cookieValue) {
      try {
        const urlDecodedString = decodeURIComponent(cookieValue);
        const formattedStringForJSONParsing = formatStringForJSONParse(urlDecodedString);
        return JSON.parse(formattedStringForJSONParsing) as ConsentData; // Parse JSON string into an object
      } catch (e) {
        ErrorManagement.exception({
          exception: `Error parsing cookie value: ${e}`,
          tags: {
            fileLocation: 'recommendations-helper.ts',
          },
          fingerprint: ['recommendations-helper'],
        });
        return undefined;
      }
    }
  }
  return undefined;
};

/**
 *
 * @returns flag to determine whether cookie bot is initialized and loaded
 * @deprecated Use `useConsent()` instead
 */
export const checkCookieBotReinitialized = () => window?.Cookiebot?.consent || window?.CookieConsent?.consent;
